import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { Media } from '../lib/types';
import { VideoPlayer } from './VideoPlayer';
import './MediaCarousel.css';

interface Props {
  /** Ordered media items (images + videos) rendered in sequence. */
  items: Media[];
  /** Fallback aspect ratio for items without their own `aspect_ratio`. */
  aspect?: number;
  /** Fires when an image slide is clicked, with that item's index. */
  onOpenLightbox?: (index: number) => void;
  /** Pager style under the viewport: 'dots' (default) or a 'thumbs' filmstrip
   *  (Saleroom lot gallery). */
  variant?: 'dots' | 'thumbs';
  /** Reports the actual rendered width (px) of the centred, width-capped media
   *  viewport whenever it changes, so a parent can align chrome (e.g. the post
   *  like/share row) to the real media box instead of the full container. More
   *  reliable than recomputing from aspect ratio (videos / failed media). */
  onViewportWidth?: (w: number) => void;
}

const SWIPE_THRESHOLD = 48; // px of horizontal travel before a swipe pages

export function MediaCarousel({ items, aspect = 16 / 9, onOpenLightbox, variant = 'dots', onViewportWidth }: Props) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const baseId = useId();

  // Measure the real (width-capped, centred) media box and report it up, so a
  // parent can align chrome to the actual media edge. We measure the ACTIVE
  // slide's media element - it's the capped box in both layouts (multi: the
  // viewport is capped; single: the media is capped + centred inside a
  // full-width viewport). A ResizeObserver catches ratio-driven resizes (paging
  // portrait<->landscape, late image load) without us tracking the aspect ratio.
  useEffect(() => {
    const root = viewportRef.current;
    if (!root || !onViewportWidth || typeof ResizeObserver === 'undefined') return;
    const el =
      root.querySelector<HTMLElement>('.carousel__slide:not([aria-hidden="true"]) .carousel__media') ??
      root.querySelector<HTMLElement>('.carousel__media') ??
      root;
    const report = () => onViewportWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(report);
    ro.observe(el);
    report();
    return () => ro.disconnect();
  }, [onViewportWidth, index, items.length]);
  // Pointer-drag bookkeeping for swipe paging.
  const dragStartX = useRef<number | null>(null);
  const dragDelta = useRef(0);

  // Resolved aspect ratio per slide. Seeded from each item's stored ratio (when
  // present) and refined to the true measured ratio once the image/video loads,
  // so the viewport can snap its box to the ACTIVE slide instead of locking to
  // the tallest one.
  const [ratios, setRatios] = useState<Record<number, number>>({});
  const reportRatio = useCallback((i: number, ar: number) => {
    if (!(ar > 0)) return;
    setRatios((m) => (m[i] === ar ? m : { ...m, [i]: ar }));
  }, []);

  const count = items.length;
  const clamp = useCallback(
    (i: number) => Math.max(0, Math.min(count - 1, i)),
    [count],
  );
  const goTo = useCallback((i: number) => setIndex((cur) => clamp(i ?? cur)), [clamp]);
  const prev = useCallback(() => setIndex((i) => clamp(i - 1)), [clamp]);
  const next = useCallback(() => setIndex((i) => clamp(i + 1)), [clamp]);

  // The box follows the active slide's ratio: measured first, then the stored
  // ratio, then the 16/9 fallback until something loads.
  const activeAr = ratios[index] ?? items[index]?.aspect_ratio ?? aspect;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
  }

  function onPointerDown(e: React.PointerEvent) {
    dragStartX.current = e.clientX;
    dragDelta.current = 0;
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragStartX.current === null) return;
    dragDelta.current = e.clientX - dragStartX.current;
  }
  function onPointerEnd() {
    if (dragStartX.current === null) return;
    const delta = dragDelta.current;
    dragStartX.current = null;
    dragDelta.current = 0;
    if (delta <= -SWIPE_THRESHOLD) next();
    else if (delta >= SWIPE_THRESHOLD) prev();
  }

  if (count === 0) return null;

  // Single item - render cleanly with no controls.
  if (count === 1) {
    return (
      <div className="carousel carousel--single">
        <div className="carousel__viewport" ref={viewportRef}>
          <div className="carousel__slide">
            <Slide item={items[0]} index={0} aspect={aspect} onOpenLightbox={onOpenLightbox} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label="Media gallery"
      tabIndex={0}
      onKeyDown={onKeyDown}
      style={{ ['--active-ar' as string]: String(activeAr) } as React.CSSProperties}
    >
      <div className="carousel__stage">
        <button
          type="button"
          className="carousel__arrow carousel__arrow--prev"
          onClick={prev}
          disabled={index === 0}
          aria-label="Previous slide"
        >
          <Chevron dir="left" />
        </button>

        <div
          className="carousel__viewport"
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onPointerLeave={onPointerEnd}
        >
          <div
            ref={trackRef}
            className="carousel__track"
            style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
          >
            {items.map((item, i) => (
              <div
                key={item.id}
                className="carousel__slide"
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${count}`}
                aria-hidden={i !== index}
                id={`${baseId}-slide-${i}`}
              >
                <Slide
                  item={item}
                  index={i}
                  aspect={aspect}
                  active={i === index}
                  onOpenLightbox={onOpenLightbox}
                  onRatio={(ar) => reportRatio(i, ar)}
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="carousel__arrow carousel__arrow--next"
          onClick={next}
          disabled={index === count - 1}
          aria-label="Next slide"
        >
          <Chevron dir="right" />
        </button>
      </div>

      {variant === 'thumbs' ? (
        <div className="carousel__thumbs" role="tablist" aria-label="Choose image">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`carousel__thumb ${i === index ? 'carousel__thumb--on' : ''}`}
              aria-selected={i === index}
              aria-label={`View ${i + 1} of ${count}`}
              aria-controls={`${baseId}-slide-${i}`}
              onClick={() => goTo(i)}
            >
              <img src={item.thumbnail_url ?? undefined} alt="" loading="lazy" />
              {item.kind === 'video' && (
                <span className="carousel__thumb-play" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="carousel__dots" role="tablist" aria-label="Choose slide">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`carousel__dot ${i === index ? 'carousel__dot--on' : ''}`}
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1} of ${count}`}
              aria-controls={`${baseId}-slide-${i}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}

      <span className="carousel__live" aria-live="polite">
        Slide {index + 1} of {count}
      </span>
    </div>
  );
}

function Slide({
  item,
  index,
  aspect,
  active,
  onOpenLightbox,
  onRatio,
}: {
  item: Media;
  index: number;
  aspect: number;
  active?: boolean;
  onOpenLightbox?: (index: number) => void;
  /** Reports the slide's resolved aspect ratio up to the carousel. */
  onRatio?: (ar: number) => void;
}) {
  const ratio = item.aspect_ratio ?? aspect;
  // Snap the slide to the image's real ratio once it loads - the stored
  // aspect_ratio is often null, so we measure naturalWidth/Height to avoid
  // letterboxing against the 16/9 fallback.
  const [naturalAr, setNaturalAr] = useState<number | null>(null);
  const ar = naturalAr ?? ratio;

  if (item.kind === 'video') {
    // Lazy-mount the player only for the active slide. Off-screen video slides
    // show a lightweight poster instead, so Chrome never lays out the native
    // controls while transformed off-screen (fixes squished/broken control bar
    // and portrait controls rendering outside the box), and paging away
    // UNMOUNTS the player - triggering VideoPlayer's hls.destroy() cleanup so
    // the previous clip stops playing (only one HLS stream ever active).
    // `active === undefined` (single-item carousel) is treated as active.
    return (
      <div className="carousel__media carousel__media--video">
        {active !== false ? (
          <VideoPlayer mediaId={item.id} poster={item.thumbnail_url} aspect={ratio} onAspect={onRatio} />
        ) : (
          <img
            src={item.thumbnail_url ?? undefined}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </div>
    );
  }

  const clickable = !!onOpenLightbox;
  return (
    <button
      type="button"
      className="carousel__media carousel__media--image"
      style={{ ['--media-ar' as string]: String(ar) } as React.CSSProperties}
      onClick={clickable ? () => onOpenLightbox?.(index) : undefined}
      disabled={!clickable}
      tabIndex={active === false ? -1 : 0}
      aria-label={clickable ? 'Open image' : undefined}
    >
      <img
        src={item.thumbnail_url ?? undefined}
        alt=""
        loading={index === 0 ? 'eager' : 'lazy'}
        onLoad={(e) => {
          const im = e.currentTarget;
          if (im.naturalWidth > 0 && im.naturalHeight > 0) {
            const measured = im.naturalWidth / im.naturalHeight;
            setNaturalAr(measured);
            onRatio?.(measured);
          }
        }}
      />
      {clickable && (
        <span className="carousel__expand" aria-hidden="true">
          <ExpandIcon />
        </span>
      )}
    </button>
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 3H5a2 2 0 0 0-2 2v4m18 0V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4m12-6v4a2 2 0 0 1-2 2h-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
